import { useState } from "react";
import { Loader2, FileText } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAssetAllocation } from "@/hooks/useAssetAllocation";
import { useFactsheetDownload } from "@/hooks/useFactsheetDownload";
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from "recharts";

interface AssetAllocation {
  category: string;
  percentage: number;
  subcategories?: Array<{ name: string; percentage: number }>;
}

// Process allocation data to combine categories (same logic as portfolio pages)
const processAllocation = (allocation: AssetAllocation[]) => {
  const processed = allocation.reduce((acc, item) => {
    const categoryLower = item.category.toLowerCase();
    
    if (categoryLower === 'gcc equities' || categoryLower === 'equities' || categoryLower === 'international equities') {
      const otherEquitiesIndex = acc.findIndex(a => a.category === 'Other Equities');
      if (otherEquitiesIndex !== -1) {
        acc[otherEquitiesIndex].percentage = parseFloat((acc[otherEquitiesIndex].percentage + item.percentage).toFixed(2));
      } else {
        acc.push({ ...item, category: 'Other Equities', percentage: parseFloat(item.percentage.toFixed(2)) });
      }
    } else if (categoryLower === 'fixed interest') {
      const globalFixedIndex = acc.findIndex(a => a.category === 'Global Fixed Interest');
      if (globalFixedIndex !== -1) {
        acc[globalFixedIndex].percentage = parseFloat((acc[globalFixedIndex].percentage + item.percentage).toFixed(2));
      } else {
        acc.push({ ...item, category: 'Global Fixed Interest', percentage: parseFloat(item.percentage.toFixed(2)) });
      }
    } else if (categoryLower === 'unknown') {
      const otherIndex = acc.findIndex(a => a.category.toLowerCase() === 'others' || a.category.toLowerCase() === 'other');
      if (otherIndex !== -1) {
        acc[otherIndex].percentage = parseFloat((acc[otherIndex].percentage + item.percentage).toFixed(2));
      } else {
        acc.push({ ...item, category: 'Other', percentage: parseFloat(item.percentage.toFixed(2)) });
      }
    } else if (categoryLower === 'others') {
      acc.push({ ...item, category: 'Other', percentage: parseFloat(item.percentage.toFixed(2)) });
    } else {
      acc.push({ ...item, percentage: parseFloat(item.percentage.toFixed(2)) });
    }
    return acc;
  }, [] as AssetAllocation[]);
  
  return [...processed].sort((a, b) => b.percentage - a.percentage);
};

const pieColors = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1', '#14b8a6', '#a855f7'
];

interface PortfolioAllocationViewProps {
  portfolioCode: string;
}

const PortfolioAllocationView = ({ portfolioCode }: PortfolioAllocationViewProps) => {
  const { allocation, loading } = useAssetAllocation(portfolioCode);
  
  const sortedAllocation = processAllocation(allocation);
  
  const pieData = sortedAllocation
    .filter(item => item.percentage > 1)
    .map((item, index) => ({
      name: item.category,
      value: item.percentage,
      color: pieColors[index % pieColors.length]
    }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (sortedAllocation.length === 0) {
    return <p className="text-muted-foreground text-center py-8">No allocation data available for {portfolioCode}</p>;
  }

  return (
    <div className="space-y-6">
      {/* Top Allocations Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {sortedAllocation.slice(0, 8).map((item, index) => (
          <div key={index} className="text-center p-3 bg-muted/30 rounded-lg">
            <div className="text-2xl font-bold text-primary">{item.percentage.toFixed(2)}%</div>
            <div className="text-sm font-medium">{item.category}</div>
          </div>
        ))}
      </div>
      
      {/* Pie Chart */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
              label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
              labelLine={false}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number) => [`${value.toFixed(2)}%`, 'Allocation']} />
            <Legend wrapperStyle={{ paddingTop: '20px' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Detailed Allocation Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Asset Category</TableHead>
              <TableHead className="text-right">Allocation %</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedAllocation.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell className="font-medium">{item.category}</TableCell>
                <TableCell className="text-right">
                  <span className="text-lg font-bold text-primary">{item.percentage.toFixed(2)}%</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const portfolios = [
  { name: "P1", label: "CBAM P1" },
  { name: "P2", label: "CBAM P2" },
  { name: "P3", label: "CBAM P3" },
  { name: "P4", label: "CBAM P4" },
  { name: "P5", label: "CBAM P5" },
  { name: "R1", label: "CBAM R1" },
  { name: "R2", label: "CBAM R2" },
  { name: "R3", label: "CBAM R3" },
  { name: "R4", label: "CBAM R4" },
  { name: "R5", label: "CBAM R5" },
];

export const PortfolioAllocationTabs = () => {
  const [selectedPortfolio, setSelectedPortfolio] = useState("P3");
  const { downloadFactsheet, loading: factsheetLoading } = useFactsheetDownload();

  return (
    <div className="space-y-6">
      <Tabs value={selectedPortfolio} onValueChange={setSelectedPortfolio}>
        <TabsList className="grid grid-cols-5 lg:grid-cols-10 w-full h-auto">
          {portfolios.map((p) => (
            <TabsTrigger key={p.name} value={p.name} className="text-xs px-2 py-1.5">
              {p.name}
            </TabsTrigger>
          ))}
        </TabsList>
        
        {portfolios.map((p) => (
          <TabsContent key={p.name} value={p.name} className="mt-6">
            <h3 className="text-lg font-semibold mb-4">{p.label} Asset Allocation</h3>
            <PortfolioAllocationView portfolioCode={p.name} />
          </TabsContent>
        ))}
      </Tabs>

      <Separator />

      {/* Factsheet Links */}
      <div>
        <h3 className="font-semibold mb-2">Link to CBAM Factsheets</h3>
        <p className="text-xs text-muted-foreground mb-4">Data Source: Supabase Storage via factsheet-manager edge function</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {portfolios.map((portfolio) => (
            <Button 
              key={portfolio.name} 
              variant="outline" 
              size="sm" 
              className="justify-start"
              onClick={() => downloadFactsheet(portfolio.name)}
              disabled={factsheetLoading}
            >
              <FileText className="h-4 w-4 mr-2" />
              {portfolio.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};
